import { useQuery } from '@pinia/colada'

import { computed } from 'vue'

import useOuraAuth from '#web-oura-tools/auth'

import { useAxios } from '#vue-shared-lib/composables/axios'

import type { PermissionGroupDto, PermissionsResponse } from '#ourahub-intra/types/permissions'
import type { OuraUser } from '#web-oura-tools/types/user'

import { normalizeGroupKey } from '#web-oura-tools/auth/oura-user'
import Permission, { PermissionGroup } from '#web-oura-tools/auth/permissions'

interface PermissionsData {
  groups: PermissionGroup[]
  permissions: Permission[]
}

const EMPTY_DATA: PermissionsData = { groups: [], permissions: [] }

const queryKeysPermissions = {
  root: (uid: string, env: string) => ['permissions', uid, env] as const,
}

export function usePermissionsQuery() {
  const ouraAuth = useOuraAuth()

  const { axiosClient } = useAxios({
    agent: 'ourahub-intra',
    url: import.meta.env.VITE_OUTO_API_URL,
    version: import.meta.env.VITE_APP_VERSION,
  })

  const query = useQuery({
    key: () => queryKeysPermissions.root(ouraAuth.user.value?.id ?? '', ouraAuth.appEnv.value),
    enabled: () => !!ouraAuth.user.value,
    initialData: (): PermissionsData => EMPTY_DATA,
    query: async (): Promise<PermissionsData> => {
      const user = ouraAuth.user.value

      if (!user) return EMPTY_DATA

      try {
        const { data } = await axiosClient.get<PermissionsResponse>('/api/v1/permissions')

        if (!data) return EMPTY_DATA

        return buildPermissionsData(user, data)
      } catch (error) {
        console.error('Failed to fetch permissions:', error)

        return EMPTY_DATA
      }
    },
  })

  const myGroups = computed(() => query.data.value.groups.filter((g) => g.isMyGroup))

  const deniersByKey = computed(() => {
    const map: Record<string, string[]> = {}
    for (const group of myGroups.value) {
      for (const permission of group.deny) {
        ;(map[permission.normalizedKey] ??= []).push(group.name)
      }
    }
    return map
  })

  const grantersByKey = computed(() => {
    const map: Record<string, string[]> = {}
    for (const group of myGroups.value) {
      for (const permission of group.allow) {
        ;(map[permission.normalizedKey] ??= []).push(group.name)
      }
    }
    return map
  })

  return { ...query, myGroups, deniersByKey, grantersByKey }
}

/**
 * Builds the permissions/groups view model for a user from the API response.
 *
 * The `/api/v1/permissions` endpoint doesn't return every group the user belongs to.
 * Groups missing from the response are surfaced as synthetic groups whose single granted
 * permission is the group name itself. The membership permission's normalizedKey is the
 * group's normalized key (`groupXxx`), so toggling it adds the group to OuraOverrides and
 * drops the user from the group (see OuraUser.activeGroups).
 */
export function buildPermissionsData(user: OuraUser, data: PermissionsResponse | undefined): PermissionsData {
  const isMyGroup = (group: PermissionGroupDto): boolean => user.hasPermission(group.key)

  const apiGroupDtos = data?.groups ?? []

  const apiGroups = apiGroupDtos.map(
    (group) =>
      new PermissionGroup(
        group.key,
        group.allowPermissions.map((p) => new Permission(p.key, p.description, user)),
        group.denyPermissions.map((p) => new Permission(p.key, p.description, user)),
        isMyGroup(group),
      ),
  )

  const apiGroupKeys = new Set(apiGroupDtos.map((g) => normalizeGroupKey(g.key)))
  const seen = new Set<string>()
  const syntheticGroups = user.groups.flatMap((g) => {
    const normalizedKey = normalizeGroupKey(g)
    if (apiGroupKeys.has(normalizedKey) || seen.has(normalizedKey)) return []
    seen.add(normalizedKey)
    const membership = new Permission(g, '', user, normalizedKey)
    // isMyGroup is forced true so the synthetic group stays listed even after its membership
    // toggle is flipped off (which removes it from activeGroups); otherwise it would vanish
    // and there'd be no way to toggle it back on.
    return [new PermissionGroup(g, [membership], [], true)]
  })

  const permissions = (data?.permissions ?? []).map((p) => new Permission(p.key, p.description, user))

  const pseudoGroups = buildNonGroupPermissionsGroup(user, [...apiGroups, ...syntheticGroups], permissions)

  return {
    permissions,
    groups: [...apiGroups, ...syntheticGroups, ...pseudoGroups],
  }
}

const NON_GROUP_PERMISSIONS_KEY = 'Non-group permissions'

/**
 * Builds a UI-only pseudo group holding every active right the user didn't gain from any of their groups
 * This group should be empty in production. In sandbox, outo-cloud gives extra permissions that are not in any group
 */
function buildNonGroupPermissionsGroup(
  user: OuraUser,
  allGroups: PermissionGroup[],
  catalog: Permission[],
): PermissionGroup[] {
  const grantedByGroups = new Set(
    allGroups.filter((g) => g.isMyGroup).flatMap((g) => g.allow.map((p) => p.normalizedKey)),
  )

  const catalogByKey = new Map(catalog.map((p) => [p.normalizedKey, p]))

  // Uses raw rights (not activeRights) so an overridden-off permission stays listed and re-enableable
  const nonGroupPermissions = user.rights
    .filter((right) => !grantedByGroups.has(right))
    .map((right) => catalogByKey.get(right) ?? new Permission(right, '', user, right))

  if (nonGroupPermissions.length === 0) return []

  return [new PermissionGroup(NON_GROUP_PERMISSIONS_KEY, nonGroupPermissions, [], true, true)]
}
